import { styleListKeys } from './styleList';

/**
 * Styles nommés du thème d'un site : les listes du configurateur classique
 * (`tableStyles`, `infoCardStyles`…) de `theme.configuration`.
 *
 * Contrairement aux styles génériques des galeries (de simples jeux de valeurs
 * recopiés), un style du thème est RÉFÉRENCÉ : le bloc porte `themeStyleId` et
 * suit le style tant qu'on ne le règle pas à la main. Il garde aussi une copie
 * des valeurs, qui prend le relais si le style disparaît du thème.
 *
 * Aucun style n'existe d'office : sans style, un bloc déposé reste tel quel et
 * la génération garde ceux du document. Un style est épinglé Défaut
 * (`…DefaultId`) : celui des blocs déposés et la réponse proposée à la
 * génération. Rien d'épinglé (thème d'avant, Défaut supprimé) : le premier.
 */

export const THEME_STYLE_LISTS = {
  table: 'tableStyles',
  infoCard: 'infoCardStyles',
  accordion: 'accordionPanelStyles',
  // Composants propres au site : leurs styles sont au format du bloc.
  button: 'buttonStyles',
  carousel: 'carouselStyles',
  tabs: 'tabsStyles',
  timeline: 'timelineStyles',
  video: 'videoStyles',
  imageComparison: 'imageComparisonStyles',
  hotspotImage: 'hotspotImageStyles',
  scrollStory: 'scrollStoryStyles',
};

export const THEME_STYLE_KINDS = Object.keys(THEME_STYLE_LISTS);

export function themeStylesOf(configuration, kind) {
  return configuration?.[THEME_STYLE_LISTS[kind]] || [];
}

/** Les clés de `configuration` d'un type de composant. */
export function themeStyleKeys(kind) {
  return styleListKeys(THEME_STYLE_LISTS[kind]);
}

/** Les identifiants du configurateur sont des nombres ou, venus d'une
 *  section liée ou d'un thème d'avant, des chaînes. */
export function themeStyleById(styles, id) {
  if (id == null) return null;
  return (styles || []).find((style) => String(style.id) === String(id)) || null;
}

export function defaultThemeStyle(configuration, kind) {
  const styles = themeStylesOf(configuration, kind);
  const pinnedId = configuration?.[themeStyleKeys(kind).defaultId];
  return themeStyleById(styles, pinnedId) || styles[0] || null;
}

const sameValue = (a, b) => (
  a && typeof a === 'object' ? JSON.stringify(a) === JSON.stringify(b) : a === b
);

/**
 * La dernière édition a-t-elle réglé à la main une valeur du style (déjà
 * convertie) ? Seules comptent les clés du style qui viennent de changer ET
 * ne valent pas la valeur du style :
 * - la copie du bloc peut dater d'avant une retouche du style : une édition
 *   hors style (pied de tableau…) ne doit pas le détacher pour autant ;
 * - choisir un style du thème recopie ses valeurs : ce n'est pas une retouche.
 */
export function editsThemeStyle(values, lastValues, styleValues) {
  return Object.entries(styleValues).some(([key, value]) => (
    !sameValue(values?.[key], lastValues?.[key]) && !sameValue(values?.[key], value)
  ));
}

/**
 * Les valeurs d'un bloc qui quitte son style parce qu'on vient d'en régler une
 * à la main : celles du style pour tout ce que l'édition n'a pas touché — la
 * copie du bloc peut dater d'avant une retouche du style — et l'édition pour
 * le reste. Le bloc ne doit pas changer d'aspect au moment où il se détache.
 */
export function detachedValues(values, lastValues, styleValues) {
  if (!styleValues) return values;
  const next = { ...(values || {}) };
  Object.entries(styleValues).forEach(([key, value]) => {
    if (sameValue(values?.[key], lastValues?.[key])) next[key] = value;
  });
  return next;
}
